import React, { useState, useRef, useEffect } from "react";
import zoomPlugin from "chartjs-plugin-zoom";
import { Button, Modal } from "react-bootstrap";
import { Chart } from "react-chartjs-2";
import { useSelector, useDispatch } from "react-redux";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useParams, useHistory } from "react-router-dom";
import {
  getEegCompareGraphs,
  getEegGraphFilter,
} from "../../actions/graphActions";
import fastBackwardIcon from "../../assets/img/svg/fast-backward-icon.svg";
import backwardIcon from "../../assets/img/svg/ten_second_back.svg";
import playIcon from "../../assets/img/svg/play-icon.svg";
import pauseIcon from "../../assets/img/svg/pause-icon.svg";
import fastForwardIcon from "../../assets/img/svg/fast-forward-icon.svg";
import forwardIcon from "../../assets/img/svg/ten_second_front.svg";
import fiveSecondForward from "../../assets/img/svg/five_second_front.svg";
import fiveSecondBackward from "../../assets/img/svg/five_second_back.svg";
import BackIcon from "../../assets/img/svg/back-icon.svg";
import { formatDate, formatDateTime } from "../../helper";
import { ArrowForwardIos, ArrowBackIos } from "@mui/icons-material";
import LinearWithValueLabel from "../loader/LinearLoaderLablel";
import zoomInIcon from "../../assets/img/svg/zoom-in-icon.svg";
import zoomOutIcon from "../../assets/img/svg/zoom-out-icon.svg";
import resetZoomIcon from "../../assets/img/svg/reset-zoom-icon.svg";
import enhanceIcon from "../../assets/img/enhance_icon.svg";
import resetFilterIcon from "../../assets/img/resetFilter.svg";
import "../recording2Updated/recording2.css";
import CircularLabelLoader from "../loader/CircularLabelLoader";
import Tooltip from "@mui/material/Tooltip";

const conditionMap = {
  1: "seizure",
  2: "concussion",
  3: "stroke",
  4: "Normal",
  5: "Altered Mental Status",
  0: "0",
};
const genderMap = {
  1: "M",
  2: "F",
};

Chart.register(zoomPlugin);

const CompareVisits = (props) => {
  const history = useHistory();
  const dispatch = useDispatch();

  const [lffValue, setLffValue] = useState("Select");
  const [hffValue, setHffValue] = useState("Select");
  const [notchValue, setNotchValue] = useState("Select");
  const [alert, setAlert] = useState();

  const [charts, setCharts] = useState([]);
  const chartRefs = useRef([]);
  const [dataCount, setDataCount] = useState(0);
  const [dataLength, setDataLength] = useState(null);

  const [eegData, setEegData] = useState(null);
  const [patientData, setPatientData] = useState();

  const [filteredEegData, setFilteredEegData] = useState(null);
  const [isFilter, setIsFilter] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingFilter, setLoadingFilter] = useState(false);

  const [isPlaying, setIsPlaying] = useState(false);
  const intervalRef = useRef(null);
  const token = useSelector((state) => state.loginData.token);
  const [requestStatus, setRequestStatus] = useState(false);

  const { eegCompareData } = useSelector((state) => state.eegCompareGraphs);
  const [progress, setProgress] = useState(1);
  const [currentLocationIndex, setCurrentLocationIndex] = useState(0);

  const [zoomState, setZoomState] = useState([
    { yMin: undefined, yMax: undefined },
    { yMin: undefined, yMax: undefined },
    { yMin: undefined, yMax: undefined },
    { yMin: undefined, yMax: undefined },
  ]);

  // Track current pan position for proper navigation
  const [currentPanWindow, setCurrentPanWindow] = useState({ min: 0, max: 10 });
  const [scrollbarPosition, setScrollbarPosition] = useState(0);
  const [totalDataDuration, setTotalDataDuration] = useState(0);

  // Trimming removed per latest requirements

  // Constants for EEG visualization
  const WINDOW_SIZE_SECONDS = 10; // Default viewing window size

  const searchParams = new URLSearchParams(window.location.search);
  const ids = searchParams.getAll("id").map(Number);

  const handleBack = () => {
    window.history.back();
  };

  const handleLocationChange = (direction) => {
    if (direction === "backward" && canNavigateLocationBackward()) {
      setCurrentLocationIndex((prev) => prev - 1);
      // Reset filters when changing location
      resetFilterOnly();
    } else if (direction === "forward" && canNavigateLocationForward()) {
      setCurrentLocationIndex((prev) => prev + 1);
      // Reset filters when changing location
      resetFilterOnly();
    }
  };

  // Fix scrollbar handler to properly update chart bounds
  const handleScrollbarChange = (e) => {
    const newPosition = parseInt(e.target.value); // Cast to integer

    // Prevent moving backward below 10s
    const minAllowedPosition = 10;
    const clampedPosition = Math.max(newPosition, minAllowedPosition);

    // Get total duration and ensure we don't exceed it
    const totalDuration = getTotalDataDuration();
    const maxAllowedPosition = Math.max(minAllowedPosition, totalDuration);
    const finalPosition = Math.min(clampedPosition, maxAllowedPosition);

    setScrollbarPosition(finalPosition);

    // Calculate window bounds directly from slider position
    // Slider shows the end of the current window
    const windowEnd = finalPosition;
    const windowStart = Math.max(0, windowEnd - WINDOW_SIZE_SECONDS);

    // Update current pan window state
    setCurrentPanWindow({ min: windowStart, max: windowEnd });

    // Calculate dataCount for synchronization with window buttons
    const newDataCount = Math.floor(windowStart / WINDOW_SIZE_SECONDS);
    setDataCount(newDataCount);

    // Directly update all charts' x-axis bounds with integer bounds
    chartRefs.current.forEach((canvasRef) => {
      if (canvasRef?.chart && canvasRef.chart.options?.scales?.x) {
        let xMin = Math.floor(windowStart);
        let xMax = Math.ceil(windowEnd);

        // Ensure at least 1 second span to prevent x-axis disappearing
        if (xMax - xMin < 1) {
          xMax = xMin + 1;
        }

        canvasRef.chart.options.scales.x.min = xMin;
        canvasRef.chart.options.scales.x.max = xMax;
        // Ensure tick settings are preserved
        canvasRef.chart.options.scales.x.ticks.stepSize = 1;
        canvasRef.chart.options.scales.x.ticks.autoSkip = false;
        canvasRef.chart.update("none");
      }
    });
  };

  // Fix the sync effect to properly update slider position
  useEffect(() => {
    // Sync slider position when dataCount changes (from window navigation)
    const totalDuration = getTotalDataDuration();
    const newSliderPosition = Math.min(
      (dataCount + 1) * WINDOW_SIZE_SECONDS,
      totalDuration
    ); // +1 because slider shows window end
    if (Math.abs(newSliderPosition - scrollbarPosition) > 0.1) {
      setScrollbarPosition(newSliderPosition);
    }
  }, [dataCount, totalDataDuration]);

  // Helper function to calculate time window
  const getTimeWindow = (dataCount, windowSize = WINDOW_SIZE_SECONDS) => {
    const startTime = dataCount * windowSize;
    const endTime = startTime + windowSize;
    return { startTime, endTime };
  };

  // Helper function to get total data duration
  const getTotalDataDuration = () => {
    if (
      !eegData?.visit_0?.[currentLocationIndex]?.CH1_Voltage ||
      eegData.visit_0[currentLocationIndex].CH1_Voltage.length === 0
    )
      return 0;

    // Calculate total duration from actual samples, not chunk count
    const totalSamples = eegData.visit_0[
      currentLocationIndex
    ].CH1_Voltage.reduce((sum, chunk) => sum + chunk.length, 0);
    const originalDuration = totalSamples / 250; // 250 samples per second

    // Round up to next whole second to prevent x-axis disappearing
    return Math.ceil(originalDuration);
  };

  // Check if we can navigate forward/backward considering both segments and pan position
  const canNavigateBackward = () => {
    return currentPanWindow.min > 0;
  };

  const canNavigateForward = () => {
    const totalDuration = getTotalDataDuration();
    return currentPanWindow.max < totalDuration;
  };

  // Check if we can navigate between locations
  const canNavigateLocationBackward = () => {
    return currentLocationIndex > 0;
  };

  const canNavigateLocationForward = () => {
    const visit0Length = eegCompareData?.data?.visit_0?.length || 0;
    const visit1Length = eegCompareData?.data?.visit_1?.length || 0;
    const maxLocationIndex = Math.min(visit0Length - 1, visit1Length - 1);

    return currentLocationIndex < maxLocationIndex;
  };

  // Flatten EEG data for panning functionality
  // This function converts segmented EEG data into continuous arrays for smooth horizontal panning
  const flattenEEGData = (channelData, timeData) => {
    const flatData = [];
    const flatLabels = [];

    if (!channelData || !timeData) return { flatData, flatLabels };

    // First, flatten all data as before
    const allData = [];
    const allLabels = [];

    channelData.forEach((chunk, index) => {
      if (!chunk || !timeData[index]) return;

      const baseTime = parseFloat(timeData[index][0] || "0.0"); // e.g., "0.0", "10.0"
      const step = 1 / 250; // Proper sample-based approach: 250 samples per second

      chunk.forEach((val, i) => {
        allData.push(val);
        allLabels.push(baseTime + i * step);
      });
    });

    // No trimming, use original data
    flatData.push(...allData);
    flatLabels.push(...allLabels);

    return { flatData, flatLabels };
  };

  // Update chart data when panning - synchronized for all charts
  const updateGraphOnPan = (chart, minX, maxX, labels, data, channelLabel) => {
    try {
      if (!chart || !labels || !data || labels.length === 0) return;
      if (!chart.data || !chart.data.datasets || !chart.data.datasets[0])
        return;

      // Instead of changing data, just update the scale bounds
      // This preserves the x-axis tick configuration
      if (chart.options && chart.options.scales && chart.options.scales.x) {
        // Force integer bounds to prevent x-axis disappearing
        let xMin = Math.floor(minX);
        let xMax = Math.ceil(maxX);

        // Ensure at least 1 second span to prevent x-axis disappearing
        if (xMax - xMin < 1) {
          xMax = xMin + 1;
        }

        chart.options.scales.x.min = xMin;
        chart.options.scales.x.max = xMax;

        // Get the original display setting from the chart reference
        const shouldDisplayXAxis =
          chart.shouldDisplayXAxis !== undefined
            ? chart.shouldDisplayXAxis
            : channelLabel === "CH2_Voltage";

        // Ensure proper x-axis configuration - preserve original display settings
        chart.options.scales.x.type = "linear";
        chart.options.scales.x.ticks.display = shouldDisplayXAxis;
        chart.options.scales.x.ticks.stepSize = 1;
        chart.options.scales.x.ticks.maxTicksLimit = 11;
        chart.options.scales.x.title.display = shouldDisplayXAxis;
        chart.options.scales.x.title.text = "Time (seconds)";

        // Force the chart to update with new bounds
        chart.update("none");
      }
    } catch (error) {}
  };

  // Synchronized panning for all charts
  const updateAllChartsOnPan = (sourceChart, minX, maxX) => {
    try {
      // Update the current pan window state
      setCurrentPanWindow({ min: minX, max: maxX });

      chartRefs.current.forEach((canvasRef, index) => {
        if (
          canvasRef?.chart &&
          canvasRef?.fullDataRef &&
          canvasRef.chart.options
        ) {
          // Get channel label from stored reference
          const channelLabel = canvasRef.channelLabel;

          // Just update the x-axis bounds - no data changes needed
          updateGraphOnPan(
            canvasRef.chart,
            minX,
            maxX,
            canvasRef.fullDataRef.flatLabels,
            canvasRef.fullDataRef.flatData,
            channelLabel
          );
        }
      });
    } catch (error) {}
  };

  const updateGraph = (dataCount) => {
    const location_visit0 = (
      eegCompareData?.data?.visit_0[currentLocationIndex]?.patient_data
        ?.location || "N/A"
    ).split("-");
    const location_visit1 = (
      eegCompareData?.data?.visit_1[currentLocationIndex]?.patient_data
        ?.location || "N/A"
    ).split("-");

    const datasets = [
      {
        data: eegCompareData?.data?.visit_0[currentLocationIndex],
        channelName: "CH1_Voltage",
        display: false, // No x-axis for upper graphs
        location: location_visit0[0],
        filteredDataIndex: 0,
      },
      {
        data: eegCompareData?.data?.visit_0[currentLocationIndex],
        channelName: "CH2_Voltage",
        display: false, // No x-axis for upper graphs
        location: location_visit0[1],
        filteredDataIndex: 0,
      },
      {
        data: eegCompareData?.data?.visit_1[currentLocationIndex],
        channelName: "CH1_Voltage",
        display: false, // No x-axis for upper graphs
        location: location_visit1[0],
        filteredDataIndex: 1,
      },
      {
        data: eegCompareData?.data?.visit_1[currentLocationIndex],
        channelName: "CH2_Voltage",
        display: true, // Only show x-axis for the bottom graph
        location: location_visit1[1],
        filteredDataIndex: 1,
      },
    ];

    const newCharts = datasets.map((item, i) => {
      const canvasRef = chartRefs.current[i];

      // If chart exists and has panning data, try to update existing chart
      if (canvasRef?.chart && canvasRef?.fullDataRef) {
        const { startTime, endTime } = getTimeWindow(dataCount);

        // Ensure we don't exceed the data boundaries
        const totalDuration = getTotalDataDuration();
        const adjustedStartTime = Math.max(
          0,
          Math.min(startTime, totalDuration - WINDOW_SIZE_SECONDS)
        );
        const adjustedEndTime = Math.min(
          totalDuration,
          adjustedStartTime + WINDOW_SIZE_SECONDS
        );

        // Update the current pan window state
        setCurrentPanWindow({ min: adjustedStartTime, max: adjustedEndTime });

        // Update scrollbar position to match the adjusted window
        setScrollbarPosition(adjustedEndTime);

        // Update the chart's time window
        canvasRef.chart.options.scales.x.min = adjustedStartTime;
        canvasRef.chart.options.scales.x.max = adjustedEndTime;

        // Update data for the new time window
        updateGraphOnPan(
          canvasRef.chart,
          adjustedStartTime,
          adjustedEndTime,
          canvasRef.fullDataRef.flatLabels,
          canvasRef.fullDataRef.flatData,
          item.channelName
        );

        return canvasRef.chart;
      } else {
        // Create new chart if it doesn't exist
        if (canvasRef?.chart) {
          canvasRef.chart.destroy();
          canvasRef.chart = null;
        }
        return createGraph(
          canvasRef,
          item.data,
          item.channelName,
          item.location,
          dataCount,
          item.display,
          item.filteredDataIndex
        );
      }
    });
    setCharts(newCharts);
  };

  const handleGraphButton = (type) => {
    let newCount;
    switch (type) {
      case "fast-backward":
        newCount = 0;
        stopPlaying();
        break;
      case "backward": {
        // Move window 10 seconds back, but not before 0
        const windowStart = Math.max(0, currentPanWindow.min - 10);
        const windowEnd = windowStart + WINDOW_SIZE_SECONDS;
        setCurrentPanWindow({ min: windowStart, max: windowEnd });
        setDataCount(Math.floor(windowStart / WINDOW_SIZE_SECONDS));
        setScrollbarPosition(windowEnd);
        // Update charts directly with integer bounds
        chartRefs.current.forEach((canvasRef) => {
          if (canvasRef?.chart && canvasRef.chart.options?.scales?.x) {
            let xMin = Math.floor(windowStart);
            let xMax = Math.ceil(windowEnd);

            // Ensure at least 1 second span to prevent x-axis disappearing
            if (xMax - xMin < 1) {
              xMax = xMin + 1;
            }

            canvasRef.chart.options.scales.x.min = xMin;
            canvasRef.chart.options.scales.x.max = xMax;
            canvasRef.chart.update("none");
          }
        });
        stopPlaying();
        return;
      }
      case "five-backward": {
        // Move window 5 seconds back, but not before 0
        const windowStart = Math.max(0, currentPanWindow.min - 5);
        const windowEnd = windowStart + WINDOW_SIZE_SECONDS;
        setCurrentPanWindow({ min: windowStart, max: windowEnd });
        setDataCount(Math.floor(windowStart / WINDOW_SIZE_SECONDS));
        setScrollbarPosition(windowEnd);
        // Update charts directly with integer bounds
        chartRefs.current.forEach((canvasRef) => {
          if (canvasRef?.chart && canvasRef.chart.options?.scales?.x) {
            let xMin = Math.floor(windowStart);
            let xMax = Math.ceil(windowEnd);

            // Ensure at least 1 second span to prevent x-axis disappearing
            if (xMax - xMin < 1) {
              xMax = xMin + 1;
            }

            canvasRef.chart.options.scales.x.min = xMin;
            canvasRef.chart.options.scales.x.max = xMax;
            canvasRef.chart.update("none");
          }
        });
        stopPlaying();
        return;
      }
      case "forward": {
        // Move window 10 seconds forward, but not past total duration
        const totalDuration = getTotalDataDuration();
        let windowStart = currentPanWindow.min + 10;
        let windowEnd = windowStart + WINDOW_SIZE_SECONDS;
        if (windowEnd > totalDuration) {
          windowEnd = totalDuration;
          windowStart = Math.max(0, windowEnd - WINDOW_SIZE_SECONDS);
        }
        setCurrentPanWindow({ min: windowStart, max: windowEnd });
        setDataCount(Math.floor(windowStart / WINDOW_SIZE_SECONDS));
        setScrollbarPosition(windowEnd);
        // Update charts directly with integer bounds
        chartRefs.current.forEach((canvasRef) => {
          if (canvasRef?.chart && canvasRef.chart.options?.scales?.x) {
            let xMin = Math.floor(windowStart);
            let xMax = Math.ceil(windowEnd);

            // Ensure at least 1 second span to prevent x-axis disappearing
            if (xMax - xMin < 1) {
              xMax = xMin + 1;
            }

            canvasRef.chart.options.scales.x.min = xMin;
            canvasRef.chart.options.scales.x.max = xMax;
            canvasRef.chart.update("none");
          }
        });
        stopPlaying();
        return;
      }
      case "five-forward": {
        // Move window 5 seconds forward, but not past total duration
        const totalDuration = getTotalDataDuration();
        let windowStart = currentPanWindow.min + 5;
        let windowEnd = windowStart + WINDOW_SIZE_SECONDS;
        if (windowEnd > totalDuration) {
          windowEnd = totalDuration;
          windowStart = Math.max(0, windowEnd - WINDOW_SIZE_SECONDS);
        }
        setCurrentPanWindow({ min: windowStart, max: windowEnd });
        setDataCount(Math.floor(windowStart / WINDOW_SIZE_SECONDS));
        setScrollbarPosition(windowEnd);
        // Update charts directly with integer bounds
        chartRefs.current.forEach((canvasRef) => {
          if (canvasRef?.chart && canvasRef.chart.options?.scales?.x) {
            let xMin = Math.floor(windowStart);
            let xMax = Math.ceil(windowEnd);

            // Ensure at least 1 second span to prevent x-axis disappearing
            if (xMax - xMin < 1) {
              xMax = xMin + 1;
            }

            canvasRef.chart.options.scales.x.min = xMin;
            canvasRef.chart.options.scales.x.max = xMax;
            canvasRef.chart.update("none");
          }
        });
        stopPlaying();
        return;
      }
      case "fast-forward":
        // Use ceiled duration for boundary calculation
        const totalDuration = getTotalDataDuration();
        // Calculate the last possible window that can show data up to the end
        const maxDataCount = Math.max(
          0,
          Math.ceil(totalDuration / WINDOW_SIZE_SECONDS) - 1
        );
        newCount = maxDataCount;
        stopPlaying();
        break;
      case "play":
        if (!isPlaying) {
          // Calculate max count based on ceiled duration
          const totalDuration = getTotalDataDuration();
          const maxCount = Math.max(
            0,
            Math.ceil(totalDuration / WINDOW_SIZE_SECONDS) - 1
          );

          if (dataCount >= maxCount) {
            setDataCount(0);
          }
          startPlaying();
        }
        return;
      case "pause":
        stopPlaying();
        return;
      default:
        return;
    }

    setDataCount(newCount);
    updateGraph(newCount);
  };

  const startPlaying = () => {
    setIsPlaying(true);
    intervalRef.current = setInterval(() => {
      setDataCount((prevCount) => {
        // Calculate max count based on ceiled duration
        const totalDuration = getTotalDataDuration();
        const maxCount = Math.max(
          0,
          Math.ceil(totalDuration / WINDOW_SIZE_SECONDS) - 1
        );

        if (prevCount < maxCount) {
          return prevCount + 1;
        } else {
          stopPlaying();
          return prevCount;
        }
      });
    }, 500);
  };

  const stopPlaying = () => {
    setIsPlaying(false);
    clearInterval(intervalRef.current);
  };

  useEffect(() => {
    return () => clearInterval(intervalRef.current);
  }, []);

  // Handle graph updates during play
  useEffect(() => {
    if (isPlaying && eegCompareData?.data) {
      updateGraph(dataCount);
    }
  }, [dataCount, isPlaying]);

  // Handle location changes
  useEffect(() => {
    if (eegCompareData?.data && !loading) {
      // Reset data count and pan window when location changes
      setDataCount(0);
      setCurrentPanWindow({ min: 0, max: WINDOW_SIZE_SECONDS });
      setScrollbarPosition(WINDOW_SIZE_SECONDS);

      // Destroy existing charts before creating new ones
      chartRefs.current.forEach((canvasRef) => {
        if (canvasRef?.chart) {
          canvasRef.chart.destroy();
          canvasRef.chart = null;
        }
      });

      // Get the new location data
      const visit1 = eegCompareData?.data?.visit_0[currentLocationIndex];
      const visit2 = eegCompareData?.data?.visit_1[currentLocationIndex];

      // Update patient data for new location
      setPatientData(visit1?.patient_data);

      // Update data length for new location
      setDataLength(Math.max(visit1?.length?.length, visit2?.length?.length));

      // Calculate total duration for new location considering trimming using sample-based approach
      const totalSamples =
        visit1?.CH1_Voltage?.reduce((sum, chunk) => sum + chunk.length, 0) || 0;
      const originalDuration = totalSamples / 250; // 250 samples per second
      setTotalDataDuration(originalDuration);

      // Get locations for new data
      const location_visit0 = (visit1?.patient_data?.location || "N/A").split(
        "-"
      );
      const location_visit1 = (visit2?.patient_data?.location || "N/A").split(
        "-"
      );

      // Create new charts with updated data
      const datasets = [
        {
          data: visit1,
          channelName: "CH1_Voltage",
          display: false,
          location: location_visit0[0],
          filteredDataIndex: 0,
        },
        {
          data: visit1,
          channelName: "CH2_Voltage",
          display: false,
          location: location_visit0[1],
          filteredDataIndex: 0,
        },
        {
          data: visit2,
          channelName: "CH1_Voltage",
          display: false,
          location: location_visit1[0],
          filteredDataIndex: 1,
        },
        {
          data: visit2,
          channelName: "CH2_Voltage",
          display: true,
          location: location_visit1[1],
          filteredDataIndex: 1,
        },
      ];

      const newCharts = datasets.map((item, i) =>
        createGraph(
          chartRefs.current[i],
          item.data,
          item.channelName,
          item.location,
          0,
          item.display,
          item.filteredDataIndex
        )
      );

      setCharts(newCharts);
    }
  }, [currentLocationIndex, eegCompareData]);

  const createGraph = (
    canvasRef,
    data,
    label,
    location,
    dataCount,
    display,
    filteredDataIndex
  ) => {
    if (!canvasRef) {
      return;
    }

    // Use the data passed to the function (either original or filtered)
    const sourceData = data;

    // Flatten the data for panning
    const { flatData, flatLabels } = flattenEEGData(
      sourceData?.[label] || [],
      sourceData?.length || []
    );

    // Store flattened data for panning reference
    const fullDataRef = { flatData, flatLabels };

    // Load all data initially - panning will just change the visible range
    const { startTime, endTime } = getTimeWindow(dataCount);

    // Ensure at least 1 second span for x-axis stability with integer bounds
    let xMin = Math.floor(startTime);
    let xMax = Math.ceil(endTime);
    if (xMax - xMin < 1) {
      xMax = xMin + 1;
    }

    // Use all flattened data, not just the visible window
    const initialLabels = flatLabels;
    const initialData = flatData;

    const chart = new Chart(canvasRef, {
      type: "line",
      data: {
        labels: initialLabels,
        datasets: [
          {
            data: initialData,
            backgroundColor: "rgba(0, 0, 0, 0)",
            borderColor: "rgba(0, 0, 0, 1)",
            borderWidth: 0.4,
            pointRadius: 0,
          },
        ],
      },
      options: {
        scales: {
          x: {
            type: "linear",
            grid: {
              display: true,
              drawOnChartArea: true,
              drawTicks: true,
              color: "rgba(0, 0, 0, 0.1)",
              lineWidth: 1,
            },
            display: true,
            ticks: {
              display: display, // This will be controlled by the display parameter
              stepSize: 1,
              autoSkip: false,
              align: "center",
              maxRotation: 0,
              minRotation: 0,
              padding: 0,
              font: {
                size: 13,
              },
              callback: function (value) {
                return Number.isInteger(value) ? value : "";
              },
            },
            title: {
              display: display, // This will be controlled by the display parameter
              text: "Time (seconds)",
              padding: 0,
              font: {
                size: 14,
              },
            },
            min: xMin,
            max: xMax,
          },
          y: {
            grid: {
              display: false,
            },
            ticks: {
              display: false,
            },
            offset: false,
            alignToPixels: true,
            title: {
              display: true,
              text: [location],
              font: {
                size: 14,
              },
            },
            grace: "10%",
            min: zoomState[
              filteredDataIndex * 2 + (label === "CH2_Voltage" ? 1 : 0)
            ]?.yMin,
            max: zoomState[
              filteredDataIndex * 2 + (label === "CH2_Voltage" ? 1 : 0)
            ]?.yMax,
          },
        },
        elements: {
          point: {
            radius: 0,
            hoverRadius: 0,
            hitRadius: 0,
          },
          line: {
            tension: 0,
          },
        },
        plugins: {
          tooltip: {
            enabled: false, // Disable tooltip for smoother pan and no hover data
          },
          legend: {
            display: false,
          },
          decimation: {
            enabled: true,
            algorithm: "min-max", // Best for line charts with large data
          },
          zoom: {
            pan: {
              enabled: true,
              mode: "y", // Only allow vertical dragging
              // Throttle pan updates for smoothness
              onPan: (() => {
                let rafId = null;
                let lastArgs = null;
                return function throttledOnPan(args) {
                  lastArgs = args;
                  if (!rafId) {
                    rafId = requestAnimationFrame(() => {
                      rafId = null;
                      try {
                        const { chart } = lastArgs;
                        if (!chart || !chart.scales || !chart.scales.x) return;
                        const xAxis = chart.scales.x;
                        let minX = xAxis.min;
                        let maxX = xAxis.max;
                        if (
                          !fullDataRef.flatLabels ||
                          fullDataRef.flatLabels.length === 0
                        )
                          return;
                        const totalDataDuration = Math.max(
                          ...fullDataRef.flatLabels
                        );
                        const windowSize = maxX - minX;
                        if (minX < 0) {
                          minX = 0;
                          maxX = windowSize;
                        }
                        if (maxX > totalDataDuration) {
                          maxX = totalDataDuration;
                          minX = totalDataDuration - windowSize;
                        }
                        updateAllChartsOnPan(chart, minX, maxX);
                      } catch (error) {}
                    });
                  }
                };
              })(),

              onPanComplete: ({ chart }) => {
                try {
                  if (chart && chart.scales && chart.scales.x) {
                    setCurrentPanWindow({
                      min: chart.scales.x.min,
                      max: chart.scales.x.max,
                    });

                    setDataCount(
                      Math.round(chart.scales.x.min / WINDOW_SIZE_SECONDS)
                    );
                  }
                } catch (error) {}
              },
            },
            zoom: {
              wheel: {
                enabled: false,
              },
              pinch: {
                enabled: false,
              },
              mode: "y",
            },
          },
        },
        interaction: {
          intersect: false,
          mode: "index",
        },
        maintainAspectRatio: false,
        animation: false,
        spanGaps: true,
        layout: {
          padding: {
            right: 10,
          },
        },
      },
    });

    canvasRef.chart = chart;
    canvasRef.fullDataRef = fullDataRef; // Store reference for updates
    canvasRef.channelLabel = label; // Store channel label for tick preservation

    // Store the display setting for x-axis
    chart.shouldDisplayXAxis = display;

    return chart;
  };

  const handleChartZoomIn = (index) => {
    const ref = chartRefs.current[index];
    if (ref?.chart) {
      const currentScale = ref.chart.scales.y;
      const range = currentScale.max - currentScale.min;
      const center = (currentScale.max + currentScale.min) / 2;
      const newRange = range * 0.5;
      const yMin = center - newRange / 2;
      const yMax = center + newRange / 2;
      ref.chart.options.scales.y.min = yMin;
      ref.chart.options.scales.y.max = yMax;
      ref.chart.update();
      setZoomState((prev) => {
        const updated = [...prev];
        updated[index] = { yMin, yMax };
        return updated;
      });
    }
  };

  const handleChartZoomOut = (index) => {
    const ref = chartRefs.current[index];
    if (ref?.chart) {
      const currentScale = ref.chart.scales.y;
      const range = currentScale.max - currentScale.min;
      const center = (currentScale.max + currentScale.min) / 2;
      const newRange = range * 2;
      const yMin = center - newRange / 2;
      const yMax = center + newRange / 2;
      ref.chart.options.scales.y.min = yMin;
      ref.chart.options.scales.y.max = yMax;
      ref.chart.update();
      setZoomState((prev) => {
        const updated = [...prev];
        updated[index] = { yMin, yMax };
        return updated;
      });
    }
  };

  const handleChartResetZoom = (index) => {
    const ref = chartRefs.current[index];
    if (ref?.chart) {
      ref.chart.options.scales.y.min = undefined;
      ref.chart.options.scales.y.max = undefined;
      ref.chart.update("none");
      setZoomState((prev) => {
        const updated = [...prev];
        updated[index] = { yMin: undefined, yMax: undefined };
        return updated;
      });
    }
  };

  useEffect(() => {
    const fetchEegData = async () => {
      try {
        setLoading(true);
        await dispatch(getEegCompareGraphs(token, ids));
        setLoading(false);
      } catch (error) {
        setLoading(false);
        setAlert(error.response);
        toast.error("Error fetching data", {
          autoClose: 3000,
          position: "top-right",
        });
      }
    };

    fetchEegData();
  }, [dispatch, token]);

  useEffect(() => {
    if (!loading && eegCompareData) {
      setPatientData(
        eegCompareData?.data?.visit_0[currentLocationIndex]?.patient_data
      );
      const visit1 = eegCompareData?.data?.visit_0[currentLocationIndex];
      const visit2 = eegCompareData?.data?.visit_1[currentLocationIndex];
      setDataLength(Math.max(visit1?.length?.length, visit2?.length?.length));
      setEegData(eegCompareData?.data);

      // Compute duration from samples
      const totalSamples =
        visit1?.CH1_Voltage?.reduce((sum, chunk) => sum + chunk.length, 0) || 0;
      const originalDuration = totalSamples / 250; // Proper sample-based duration
      setTotalDataDuration(originalDuration);
      // Initialize slider position to 10s after data is loaded
      setTimeout(() => {
        setScrollbarPosition(10);
        // Also initialize the pan window to match
        setCurrentPanWindow({ min: 0, max: 10 });
      }, 100);

      const location_visit0 = (visit1?.patient_data?.location || "N/A").split(
        "-"
      );

      const location_visit1 = (visit2?.patient_data?.location || "N/A").split(
        "-"
      );

      // Destroy old charts before creating new ones
      chartRefs.current.forEach((canvasRef) => {
        if (canvasRef?.chart) {
          canvasRef.chart.destroy();
          canvasRef.chart = null;
        }
      });

      const datasets = [
        {
          data: visit1,
          channelName: "CH1_Voltage",
          display: false, // No x-axis for upper graphs
          location: location_visit0[0],
          filteredDataIndex: 0,
        },
        {
          data: visit1,
          channelName: "CH2_Voltage",
          display: false, // No x-axis for upper graphs
          location: location_visit0[1],
          filteredDataIndex: 0,
        },
        {
          data: visit2,
          channelName: "CH1_Voltage",
          display: false, // No x-axis for upper graphs
          location: location_visit1[0],
          filteredDataIndex: 1,
        },
        {
          data: visit2,
          channelName: "CH2_Voltage",
          display: true, // Only show x-axis for the bottom graph
          location: location_visit1[1],
          filteredDataIndex: 1,
        },
      ];

      const visitCharts = datasets.map((item, i) =>
        createGraph(
          chartRefs.current[i],
          item.data,
          item.channelName,
          item.location,
          dataCount,
          item.display,
          item.filteredDataIndex
        )
      );

      setCharts(visitCharts);

      // Initialize pan window state
      const { startTime, endTime } = getTimeWindow(dataCount);
      setCurrentPanWindow({ min: startTime, max: endTime });
    }
    return () => {
      chartRefs.current.forEach((canvasRef) => {
        if (canvasRef?.chart) {
          canvasRef.chart.destroy();
          canvasRef.chart = null;
        }
      });
    };
  }, [loading, eegCompareData, isFilter]);

  const handleFilterChange = async (filterType, value) => {
    switch (filterType) {
      case "lff":
        setLffValue(value);
        break;
      case "hff":
        setHffValue(value);
        break;
      case "notch":
        setNotchValue(value);
        break;
      case "enhance":
        setLffValue("1Hz");
        setHffValue("70Hz");
        setNotchValue("60Hz");
      default:
        break;
    }

    if (filterType === "enhance") {
      await applyFiltersWithValues("1Hz", "70Hz", "60Hz");
    } else {
      const lff =
        filterType === "lff" ? value : lffValue === "Select" ? "N/A" : lffValue;
      const hff =
        filterType === "hff" ? value : hffValue === "Select" ? "N/A" : hffValue;
      const notch =
        filterType === "notch"
          ? value
          : notchValue === "Select"
          ? "N/A"
          : notchValue;

      if (lff !== "Select" || hff !== "Select" || notch !== "Select") {
        await applyFiltersWithValues(lff, hff, notch);
      }
    }
  };

  const applyFiltersWithValues = async (lff, hff, notch) => {
    setProgress(0);
    const simulateProgress = () => {
      let interval = setInterval(() => {
        setProgress((prevProgress) =>
          prevProgress >= 100 ? 100 : prevProgress + 1
        );
      }, 1000);
      return () => clearInterval(interval);
    };

    simulateProgress();

    setLoadingFilter(true);
    try {
      const filterData = await dispatch(
        getEegGraphFilter(
          token,
          eegCompareData?.data?.visit_detail_ids,
          lff,
          hff,
          notch
        )
      );

      if (filterData) {
        setFilteredEegData(filterData);
        setDataCount(0);
        setProgress(100);
        setIsFilter(true);
        // Reset pan window state
        setCurrentPanWindow({ min: 0, max: WINDOW_SIZE_SECONDS });
      } else {
        toast.error("Error applying filters");
        setProgress(100);
        setIsFilter(false);
      }
    } catch (error) {
      toast.error("Error applying filters");
      setProgress(100);
      setIsFilter(false);
    } finally {
      setLoadingFilter(false);
    }
  };

  const resetFilter = () => {
    setDataCount(0);
    setIsFilter(false);
    setFilteredEegData(null);
    setLffValue("Select");
    setHffValue("Select");
    setNotchValue("Select");
    // Reset pan window state
    setCurrentPanWindow({ min: 0, max: WINDOW_SIZE_SECONDS });
    // Reset location index
    setCurrentLocationIndex(0);

    // Recalculate total duration after filter reset using sample-based approach
    if (eegData) {
      const visit1 = eegData?.visit_0[currentLocationIndex];
      if (visit1) {
        const totalSamples =
          visit1?.CH1_Voltage?.reduce((sum, chunk) => sum + chunk.length, 0) ||
          0;
        const originalDuration = totalSamples / 250; // 250 samples per second
        setTotalDataDuration(originalDuration);
      }
    }
  };

  const resetFilterOnly = () => {
    setDataCount(0);
    setIsFilter(false);
    setFilteredEegData(null);
    setLffValue("Select");
    setHffValue("Select");
    setNotchValue("Select");
    // Reset pan window state
    setCurrentPanWindow({ min: 0, max: WINDOW_SIZE_SECONDS });
    // Don't reset location index

    // Recalculate total duration after filter reset using sample-based approach
    if (eegData) {
      const visit1 = eegData?.visit_0[currentLocationIndex];
      if (visit1) {
        const totalSamples =
          visit1?.CH1_Voltage?.reduce((sum, chunk) => sum + chunk.length, 0) ||
          0;
        const originalDuration = totalSamples / 250; // 250 samples per second
        setTotalDataDuration(originalDuration);
      }
    }
  };

  useEffect(() => {
    if (isFilter && filteredEegData && !loadingFilter) {
      // When filter is applied, recreate graphs with filtered data
      const location_visit0 = (
        eegCompareData?.data?.visit_0[currentLocationIndex]?.patient_data
          ?.location || "N/A"
      ).split("-");
      const location_visit1 = (
        eegCompareData?.data?.visit_1[currentLocationIndex]?.patient_data
          ?.location || "N/A"
      ).split("-");

      chartRefs.current.forEach((canvasRef) => {
        if (canvasRef?.chart) {
          canvasRef.chart.destroy();
          canvasRef.chart = null;
        }
      });

      const datasets = [
        {
          data: filteredEegData[0], // Use filtered data for visit 0
          channelName: "CH1_Voltage",
          display: false, // No x-axis for upper graphs
          location: location_visit0[0],
          filteredDataIndex: 0,
        },
        {
          data: filteredEegData[0], // Use filtered data for visit 0
          channelName: "CH2_Voltage",
          display: false, // No x-axis for upper graphs
          location: location_visit0[1],
          filteredDataIndex: 0,
        },
        {
          data: filteredEegData[1], // Use filtered data for visit 1
          channelName: "CH1_Voltage",
          display: false, // No x-axis for upper graphs
          location: location_visit1[0],
          filteredDataIndex: 1,
        },
        {
          data: filteredEegData[1], // Use filtered data for visit 1
          channelName: "CH2_Voltage",
          display: true, // Only show x-axis for the bottom graph
          location: location_visit1[1],
          filteredDataIndex: 1,
        },
      ];

      const newCharts = datasets.map((item, i) =>
        createGraph(
          chartRefs.current[i],
          item.data, // Pass the filtered data directly
          item.channelName,
          item.location,
          0, // Reset to beginning when filter is applied
          item.display,
          item.filteredDataIndex
        )
      );

      setCharts(newCharts);
      setDataCount(0); // Reset data count when filter is applied

      // Reset pan window state when filter is applied
      setCurrentPanWindow({ min: 0, max: WINDOW_SIZE_SECONDS });
    }
  }, [isFilter, filteredEegData, loadingFilter]);

  return (
    <div>
      {alert ? (
        <Modal.Dialog>
          <Modal.Header>
            <Modal.Title>Data Not Available</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p>No data was found for this patient visit</p>
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="primary"
              onClick={(e) => {
                history.goBack();
              }}
            >
              Go Back
            </Button>
          </Modal.Footer>
        </Modal.Dialog>
      ) : (
        <div className="recording2" style={{ marginBottom: "50px" }}>
          <div className="recording-graph-panel">
            <div className="info-container">
              <div className="personal-info">
                <div className="btn-back" onClick={handleBack}>
                  <span className="back-info-btn">
                    <img src={BackIcon} alt="back-icon" />
                  </span>
                  <div className="back-info-btn" onClick={handleBack}>
                    Back
                  </div>
                </div>

                <h1>{patientData?.id_number ?? "N/A"}</h1>
                <div className="info-wrapper">
                  <div className="info">
                    <p>Age / Gender: </p>
                    <p>
                      {patientData?.age || "N/A"}{" "}
                      {genderMap[patientData?.gender] || "N/A"}
                    </p>
                  </div>
                  {/* <div className="info">
                    <p>Patient#: </p>
                    <p>{patientData?.id_number || "N/A"}</p>
                  </div> */}
                  <div className="info">
                    <p>Recording Date: </p>
                    <p>
                      {patientData?.timestamp
                        ? formatDate(patientData?.timestamp)
                        : "N/A"}
                    </p>
                  </div>
                  <div className="info">
                    <p>Condition: </p>
                    <p>
                      {conditionMap[patientData?.clinical_condition] || "N/A"}
                    </p>
                  </div>
                </div>
              </div>
              {/* <div className="btns-container">
                <button className="button btn-invert-rec">Add Notes</button>
                <button className="button btn-default-rec">Share Report</button>
              </div> */}
            </div>

            <div className="eeg-graph-panel">
              {loading || loadingFilter ? (
                <div className="linear-loader">
                  {isFilter ? (
                    <CircularLabelLoader value={progress} />
                  ) : (
                    <LinearWithValueLabel increment={1} />
                  )}
                </div>
              ) : (
                <>
                  <div className="eeg-header">
                    <div className="header-stats">
                      <div className="header-stat">
                        <p>
                          Display: <span>10s</span>
                        </p>
                      </div>
                      <div className="header-stat">
                        <p>
                          Scale: <span>10uv/mm</span>
                        </p>
                      </div>
                      <div className="header-stat">
                        <p>LFF: </p>
                        <div
                          className="dropdown"
                          style={{ paddingLeft: "4px" }}
                        >
                          <button
                            className="btn green-btn2 btn-sm dropdown-toggle"
                            type="button"
                            id="dropdownMenuLFF"
                            data-toggle="dropdown"
                            aria-haspopup="true"
                            aria-expanded="false"
                          >
                            {lffValue}
                          </button>
                          <div
                            className="dropdown-menu"
                            aria-labelledby="dropdownMenuLFF"
                          >
                            <button
                              className="dropdown-item"
                              type="button"
                              onClick={() => handleFilterChange("lff", "N/A")}
                            >
                              N/A
                            </button>
                            <button
                              className="dropdown-item"
                              type="button"
                              onClick={() => handleFilterChange("lff", "1Hz")}
                            >
                              1Hz
                            </button>
                            <button
                              className="dropdown-item"
                              type="button"
                              onClick={() => handleFilterChange("lff", "5Hz")}
                            >
                              5Hz
                            </button>
                            <button
                              className="dropdown-item"
                              type="button"
                              onClick={() => handleFilterChange("lff", "15Hz")}
                            >
                              15Hz
                            </button>
                            <button
                              className="dropdown-item"
                              type="button"
                              onClick={() => handleFilterChange("lff", "20Hz")}
                            >
                              20Hz
                            </button>
                          </div>
                        </div>
                      </div>
                      <div className="header-stat">
                        <p>HFF: </p>
                        <div
                          className="dropdown"
                          style={{ paddingLeft: "4px" }}
                        >
                          <button
                            className="btn green-btn2 btn-sm dropdown-toggle"
                            type="button"
                            id="dropdownMenuHFF"
                            data-toggle="dropdown"
                            aria-haspopup="true"
                            aria-expanded="false"
                          >
                            {hffValue}
                          </button>
                          <div
                            className="dropdown-menu"
                            aria-labelledby="dropdownMenuHFF"
                          >
                            <button
                              className="dropdown-item"
                              type="button"
                              onClick={() => handleFilterChange("hff", "N/A")}
                            >
                              N/A
                            </button>
                            <button
                              className="dropdown-item"
                              type="button"
                              onClick={() => handleFilterChange("hff", "15Hz")}
                            >
                              15 Hz
                            </button>
                            <button
                              className="dropdown-item"
                              type="button"
                              onClick={() => handleFilterChange("hff", "30Hz")}
                            >
                              30 Hz
                            </button>
                            <button
                              className="dropdown-item"
                              type="button"
                              onClick={() => handleFilterChange("hff", "40Hz")}
                            >
                              40 Hz
                            </button>
                            <button
                              className="dropdown-item"
                              type="button"
                              onClick={() => handleFilterChange("hff", "70Hz")}
                            >
                              70 Hz
                            </button>
                          </div>
                        </div>
                      </div>
                      <div className="header-stat">
                        <p>Notch: </p>
                        <div
                          className="dropdown"
                          style={{ paddingLeft: "4px" }}
                        >
                          <button
                            className="btn btn-sm green-btn2 dropdown-toggle"
                            type="button"
                            id="dropdownMenuNotch"
                            data-toggle="dropdown"
                            aria-haspopup="true"
                            aria-expanded="false"
                          >
                            {notchValue}
                          </button>
                          <div
                            className="dropdown-menu"
                            aria-labelledby="dropdownMenuNotch"
                          >
                            <button
                              className="dropdown-item"
                              type="button"
                              onClick={() => handleFilterChange("notch", "N/A")}
                            >
                              N/A
                            </button>
                            <button
                              className="dropdown-item"
                              type="button"
                              onClick={() =>
                                handleFilterChange("notch", "50Hz")
                              }
                            >
                              50 Hz
                            </button>
                            <button
                              className="dropdown-item"
                              type="button"
                              onClick={() =>
                                handleFilterChange("notch", "60Hz")
                              }
                            >
                              60 Hz
                            </button>
                          </div>

                          <div className="control-buttons">
                            <Tooltip title="Reset filter">
                              <button
                                type="button"
                                onClick={() => resetFilter()}
                              >
                                <img src={resetFilterIcon} alt="reset filter" />
                              </button>
                            </Tooltip>

                            <Tooltip title="Enhance (LFF=1Hz, HFF=70Hz, Notch=60Hz)">
                              <button
                                type="button"
                                onClick={() =>
                                  handleFilterChange(
                                    "enhance",
                                    "(LFF=1Hz, HFF=70Hz, Notch=60Hz)"
                                  )
                                }
                              >
                                <img src={enhanceIcon} alt="enhance" />
                              </button>
                            </Tooltip>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="location-btns">
                      <button
                        className="btn loc-btn"
                        type="button"
                        onClick={() => handleLocationChange("backward")}
                        disabled={!canNavigateLocationBackward()}
                      >
                        <ArrowBackIos
                          style={{ fontSize: 12, marginRight: 4 }}
                        />
                        <span className="loc-name">Back</span>
                      </button>
                      <span className="loc-name">{patientData?.location}</span>
                      <button
                        className="btn loc-btn"
                        type="button"
                        onClick={() => handleLocationChange("forward")}
                        disabled={!canNavigateLocationForward()}
                      >
                        <span className="loc-name">Next</span>
                        <ArrowForwardIos
                          style={{ fontSize: 12, marginLeft: 4 }}
                        />
                      </button>
                    </div>
                  </div>
                  <div
                    className="graph-box mt-2"
                    style={{
                      position: "relative",
                      border: "1px solid #ddd",
                      borderRadius: "4px",
                      padding: "25px",
                    }}
                  >
                    <span className="fw-bold rec-data">
                      {formatDateTime(
                        eegCompareData?.data?.visit_0[currentLocationIndex]
                          ?.patient_data?.timestamp
                      )}
                    </span>
                    {[0, 1].map((chart, index) => (
                      <div
                        key={index}
                        className="canvas-wrapper"
                        style={{ position: "relative" }}
                      >
                        <canvas
                          ref={(el) => (chartRefs.current[chart] = el)}
                          height={index === 1 ? 120 : 120}
                        />
                        <div
                          style={{
                            position: "absolute",
                            top: 2,
                            right: -24,
                            display: "flex",
                            flexDirection: "column",
                            gap: 2,
                          }}
                        >
                          <button
                            className="control-button"
                            onClick={() => handleChartZoomIn(chart)}
                            title="Zoom In"
                          >
                            <img
                              src={zoomInIcon}
                              alt="zoom in"
                              style={{ width: 16, height: 16 }}
                            />
                          </button>
                          <button
                            className="control-button"
                            onClick={() => handleChartZoomOut(chart)}
                            title="Zoom Out"
                          >
                            <img
                              src={zoomOutIcon}
                              alt="zoom out"
                              style={{ width: 18, height: 18 }}
                            />
                          </button>
                          <button
                            className="control-button"
                            onClick={() => handleChartResetZoom(chart)}
                            title="Reset Zoom"
                          >
                            <img
                              src={resetZoomIcon}
                              alt="reset zoom"
                              style={{ width: 16, height: 16 }}
                            />
                          </button>
                        </div>
                      </div>
                    ))}

                    <span
                      className="fw-bold rec-data1"
                      style={{ marginTop: "20px" }}
                    >
                      {formatDateTime(
                        eegCompareData?.data?.visit_1[currentLocationIndex]
                          ?.patient_data?.timestamp
                      )}
                    </span>
                    {[2, 3].map((chart, index) => (
                      <div
                        key={index}
                        className="canvas-wrapper"
                        style={{ position: "relative" }}
                      >
                        <canvas
                          ref={(el) => (chartRefs.current[chart] = el)}
                          height={index === 1 ? 150 : 120}
                        />
                        <div
                          style={{
                            position: "absolute",
                            top: 2,
                            right: -24,
                            display: "flex",
                            flexDirection: "column",
                            gap: 2,
                          }}
                        >
                          <button
                            className="control-button"
                            onClick={() => handleChartZoomIn(chart)}
                            title="Zoom In"
                          >
                            <img
                              src={zoomInIcon}
                              alt="zoom in"
                              style={{ width: 16, height: 16 }}
                            />
                          </button>
                          <button
                            className="control-button"
                            onClick={() => handleChartZoomOut(chart)}
                            title="Zoom Out"
                          >
                            <img
                              src={zoomOutIcon}
                              alt="zoom out"
                              style={{ width: 16, height: 16 }}
                            />
                          </button>
                          <button
                            className="control-button"
                            onClick={() => handleChartResetZoom(chart)}
                            title="Reset Zoom"
                          >
                            <img
                              src={resetZoomIcon}
                              alt="reset zoom"
                              style={{ width: 18, height: 18 }}
                            />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Horizontal Scrollbar */}
                  <div
                    className="scrollbar-container"
                    style={{
                      marginTop: "10px",
                      padding: "0 20px",
                      display: "flex",
                      alignItems: "center",
                      gap: "10px",
                    }}
                  >
                    <span style={{ fontSize: "12px", color: "#666" }}>
                      0s/{Math.round(scrollbarPosition)}s
                    </span>
                    <input
                      type="range"
                      min="10"
                      max={Math.max(10, getTotalDataDuration())}
                      step="1"
                      value={scrollbarPosition}
                      onChange={handleScrollbarChange}
                      style={{
                        flex: 1,
                        height: "6px",
                        borderRadius: "3px",
                        background: "#ddd",
                        outline: "none",
                        cursor: "pointer",
                      }}
                    />
                    <span style={{ fontSize: "12px", color: "#666" }}>
                      {getTotalDataDuration()}s
                    </span>
                  </div>

                  <div
                    className="eeg-graph-controls"
                    style={{ marginBottom: "30px" }}
                  >
                    <button
                      disabled={!canNavigateBackward()}
                      className="control-button"
                      onClick={() => handleGraphButton("fast-backward")}
                    >
                      <img src={fastBackwardIcon} alt="fast backward" />
                    </button>

                    <button
                      disabled={!canNavigateBackward()}
                      className="control-button"
                      onClick={() => handleGraphButton("backward")}
                    >
                      <img src={backwardIcon} alt="backward" />
                    </button>

                    <button
                      disabled={!canNavigateBackward()}
                      className="control-button"
                      onClick={() => handleGraphButton("five-backward")}
                    >
                      <img src={fiveSecondBackward} alt="5s backward" />
                    </button>

                    <button
                      className="control-button"
                      onClick={() =>
                        handleGraphButton(isPlaying ? "pause" : "play")
                      }
                    >
                      <img
                        src={isPlaying ? pauseIcon : playIcon}
                        alt={isPlaying ? "pause" : "play"}
                      />
                    </button>

                    <button
                      disabled={!canNavigateForward()}
                      className="control-button"
                      onClick={() => handleGraphButton("five-forward")}
                    >
                      <img src={fiveSecondForward} alt="5s forward" />
                    </button>

                    <button
                      disabled={!canNavigateForward()}
                      className="control-button"
                      onClick={() => handleGraphButton("forward")}
                    >
                      <img src={forwardIcon} alt="forward" />
                    </button>

                    <button
                      disabled={!canNavigateForward()}
                      className="control-button"
                      onClick={() => handleGraphButton("fast-forward")}
                    >
                      <img src={fastForwardIcon} alt="fast forward" />
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CompareVisits;
